import { type FieldValues, useFormContext } from 'react-hook-form';
import { FilterBadgesList } from '@/components/FilterBadgesList';
import Button from '@/components/UI/Button';
import { HookForm } from '@/components/UI/HookForm/Form';
import { HookInput } from '@/components/UI/HookForm/Form/Input';
import { HookReactSingleSelect } from '@/components/UI/HookForm/Form/ReactSelect';
import { HookFormProvider } from '@/components/UI/HookForm/Provider';
import Modal from '@/components/UI/Modal';
import { changeOrderStatusOptions } from '@/features/ChangeOrder/Summary/constants';
import { useProjectPhases } from '@/hooks/ReactQueryHooks/useProjectQuery';
import type { ReactSelectOption } from '@/types/Constants';
import { isObjectEmpty } from '@/utils';

type AppliedFilters = FieldValues;

interface FilterData {
  phase: ReactSelectOption;
  daysImpacted: number;
  submitter: ReactSelectOption;
  status: ReactSelectOption;
  approver: ReactSelectOption;
  daysLeftToRespond: number;
  responseDueBy: string;
}

interface ChangeOrderFiltersFormProps {
  displayFilters: any;
  approverOptions: ReactSelectOption[];
  submitterOptions: ReactSelectOption[];
  handleAppliedFilters: (filters: any) => void;
  handleDisplayFilters: (filters: any) => void;
  hide: () => void;
}

const fieldLabels: Record<keyof FilterData, string> = {
  phase: 'Phase',
  daysImpacted: 'Days Impacted',
  submitter: 'Submitter',
  status: 'Status',
  approver: 'Approver',
  daysLeftToRespond: 'Days Left To Respond',
  responseDueBy: 'Response Due By',
};

const ChangeOrderFiltersHookForm = ({
  hide,
  submitterOptions,
  approverOptions,
  handleAppliedFilters,
  handleDisplayFilters,
}: ChangeOrderFiltersFormProps) => {
  const formMethods = useFormContext<FilterData>();
  const formValues = formMethods.watch();

  const onSubmit = (formData: FilterData) => {
    const appliedFilters: AppliedFilters = {
      phaseSlug: formData.phase?.value,
      daysImpacted: formData.daysImpacted,
      submitterId: formData.submitter?.value,
      approverId: formData.approver?.value,
      status: formData.status?.value,
      responseDueBy: formData.responseDueBy,
      daysLeftToRespond: formData.daysLeftToRespond,
    };

    const displayFilters = formData;

    handleAppliedFilters(appliedFilters);
    handleDisplayFilters(displayFilters);
    hide();
  };

  const phases = useProjectPhases();
  const phaseOptions = phases.data?.data.map((phase) => ({
    label: phase.name,
    value: phase.slug,
  }));

  const getDisplayFilters = () => {
    return Object.fromEntries(
      Object.entries(formValues)
        .filter(([_, value]) => !!value)
        .map(([key, value]) => [
          key,
          typeof value !== 'object'
            ? {
                label: fieldLabels[key as keyof FilterData],
                value: value,
              }
            : {
                label: fieldLabels[key as keyof FilterData],
                value: value.label,
              },
        ])
    );
  };

  const displayFilters = getDisplayFilters();

  return (
    <HookForm onSubmit={onSubmit}>
      <div className='p-6'>
        <h2 className='mt-0 capitalize'>Filter By</h2>
        {!isObjectEmpty(displayFilters) && (
          <FilterBadgesList
            className='pb-4'
            displayFilters={displayFilters}
            handleRemoveFilter={(key) => {
              formMethods.setValue(key as keyof FilterData, '');
            }}
          />
        )}
        <section className='grid grid-cols-2 gap-5 items-center'>
          <HookReactSingleSelect label='Phase' options={phaseOptions} placeholder='' name='phase' />
          <HookInput label='Days Impacted' type='number' name='daysImpacted' />
          <HookReactSingleSelect label='Submitter' options={submitterOptions} placeholder='' name='submitter' />
          <HookReactSingleSelect label='Status' options={changeOrderStatusOptions} placeholder='' name='status' />
          <HookReactSingleSelect
            label='Approver'
            componentClassName='col-span-2'
            options={approverOptions}
            placeholder=''
            name='approver'
          />
          <HookInput customClassName='col-span-2' label='Days Left to Respond (or less)' type='number' name='daysLeftToRespond' />
          <HookInput customClassName='col-span-2' label='Response Due By' type='date' name='responseDueBy' />
        </section>
      </div>

      <div className='flex gap-2 justify-end p-6 border-t border-gray-300'>
        <Button label='Cancel' btnStyle='neutral' onClick={hide} />
        <Button label='Apply' btnStyle='primary' btnType='submit' />
      </div>
    </HookForm>
  );
};

const FiltersForm = (props: ChangeOrderFiltersFormProps) => {
  return (
    <Modal customStyle='max-w-xl p-0'>
      <HookFormProvider defaultValues={{ ...props.displayFilters }}>
        <ChangeOrderFiltersHookForm {...props} />
      </HookFormProvider>
    </Modal>
  );
};

export default FiltersForm;
